import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Building2, BookOpenCheck, ChevronDown, Compass, FileUser, ShieldCheck, Store, Users } from "lucide-react";
import { Eyebrow, Reveal, easeOut } from "@/components/Reveal";
import { SERVICES } from "@/lib/content";

const ICONS = { FileUser, Building2, BookOpenCheck, Store, ShieldCheck, Compass };

export default function Services() {
  const [open, setOpen] = useState(null);

  return (
    <section id="services" className="bg-white py-24 lg:py-32" data-testid="services-section">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <Eyebrow>What we do</Eyebrow>
              <h2 className="mt-4 max-w-xl font-display text-4xl font-medium leading-tight tracking-tight text-[#102036] sm:text-5xl">
                Full-service accounting &amp; tax, without the runaround.
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-[#4A5568]">
              Six practices, one accountable team. Expand any service for the detail — then contact us for the advice.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const isOpen = open === i;
            return (
              <Reveal key={s.num} delay={0.06 * (i % 3)}>
                <div
                  className={`group flex h-full flex-col rounded-lg border p-8 transition-all duration-300 ${
                    isOpen
                      ? "border-[#102036]/40 bg-[#FFFFFF] shadow-[0_18px_45px_rgb(12,24,42,0.08)]"
                      : "border-[#102036]/10 bg-white hover:-translate-y-1 hover:shadow-[0_16px_40px_rgb(12,24,42,0.07)]"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-md bg-[#3682AE]/10 text-[#3682AE]">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <span className="font-display text-2xl italic text-[#102036]/20">{s.num}</span>
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-semibold text-[#102036]">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#4A5568]">{s.short}</p>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: easeOut }}
                        className="mt-5 list-none space-y-2.5 overflow-hidden border-t border-[#102036]/10 pt-5"
                      >
                          {s.details.map((d) => (
                            <li key={d} className="flex gap-2.5 text-[13px] leading-relaxed text-[#102036]/80">
                              <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[#102036]" />
                              {d}
                            </li>
                          ))}
                        </motion.ul>
                    )}
                  </AnimatePresence>
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    data-testid={`service-expand-${s.num}`}
                    aria-expanded={isOpen}
                    className="mt-6 inline-flex items-center gap-2 self-start text-[12px] font-bold uppercase tracking-[0.16em] text-[#102036] transition-colors hover:text-[#102036]"
                  >
                    {isOpen ? "Show less" : "Learn more"}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {s.page && (
                    <Link
                      to={s.page}
                      data-testid={`service-page-link-${s.num}`}
                      className="mt-3 inline-flex items-center gap-2 self-start text-[12px] font-bold uppercase tracking-[0.16em] text-[#3682AE] transition-colors hover:text-[#102036]"
                    >
                      Full advisory page <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="relative mt-16 overflow-hidden rounded-lg bg-[#102036] p-10 sm:p-14" data-testid="executive-advisory-feature">
            <div className="grain pointer-events-none absolute inset-0" aria-hidden="true" />
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#3682AE]/25 blur-3xl" aria-hidden="true" />
            <div className="relative grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-center">
              <div>
                <p className="inline-flex items-center gap-2 rounded-full border border-[#FFFFFF]/25 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.24em] text-[#FFFFFF]/80">
                  <Users className="h-3.5 w-3.5" /> Senior-level service
                </p>
                <h3 className="mt-6 font-display text-3xl font-medium leading-tight text-[#FFFFFF] sm:text-4xl">
                  Executive Advisory &amp; Business Leadership
                </h3>
                <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[#FFFFFF]/75">
                  A distinct advisory practice for business owners, executives and boards — senior guidance for
                  companies experiencing growth or transition, and for the strategic decisions that shape both.
                </p>
                <Link
                  to="/business-advisory"
                  data-testid="executive-advisory-page-link"
                  className="mt-8 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#FFFFFF] transition-colors hover:text-[#EAE7E0]"
                >
                  Explore Executive Advisory <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
              <ul className="grid grid-cols-2 gap-3 text-[13px] text-[#FFFFFF]/85">
                {["Business owners", "Executives", "Boards & directors", "High-growth companies", "Transition & succession", "Strategic decisions"].map((chip) => (
                  <li key={chip} className="rounded-md border border-[#FFFFFF]/15 px-4 py-3">{chip}</li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
